import { isAction } from '@reduxjs/toolkit';
import type { Middleware, PayloadAction } from '@reduxjs/toolkit';
import type { IEventBus } from '@shore/types';
import { changeLocation } from '../actions';
import EVENT_BUS_EVENTS from '../constants/event-bus-events';
import { history, location } from '../env/browser';
import type { RootState } from '../reducers';
import { isGravityCalendarMerchant } from '../selectors';
import { toGravityCalendarUrl } from '../utils/url';
import { matchesType } from './fx-middleware';

const NAVIGATE_TO = { type: 'APP(*)/NAVIGATE_TO' };

type UrlAction = PayloadAction<{ url?: string }>;

const isUrlAction = (action: unknown): action is UrlAction =>
  changeLocation.match(action) ||
  (isAction(action) && matchesType(NAVIGATE_TO, action));

function replaceLegacyBrowserUrl(eventBus: Pick<IEventBus, 'emit'>): void {
  const url = toGravityCalendarUrl(location.href);
  if (url === location.href) return;

  history.replaceState(history.state, '', url);
  eventBus.emit(EVENT_BUS_EVENTS.NAVIGATE, {
    payload: {
      route: `${location.pathname}${location.search}${location.hash}`,
      replace: true,
    },
  });
}

export function createGravityCalendarRedirectMiddleware(
  eventBus: Pick<IEventBus, 'emit'>
): Middleware<object, RootState> {
  return ({ getState }) =>
    (next) =>
    (action) => {
      if (
        !isUrlAction(action) ||
        !action.payload.url ||
        !isGravityCalendarMerchant(getState())
      ) {
        return next(action);
      }

      const result = next({
        ...action,
        payload: {
          ...action.payload,
          url: toGravityCalendarUrl(action.payload.url),
        },
      });

      if (changeLocation.match(action)) replaceLegacyBrowserUrl(eventBus);

      return result;
    };
}
